import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import type {
  CreateSubtaskPayload,
  CreateTaskPayload,
  Subtask,
  SubtaskProps,
  TaskProps,
  UpdateTaskPayload,
} from '~/types';
import { TaskPriority, TaskStatus } from '~/types';

import { useLoadingStore } from '../stores/loadingStore';
import { useUIStore } from '../stores/uiStore';
import { logger } from '../utils/logger';

import { useSubtasks } from './useSubtasks';
import { useTasks } from './useTasks';

export function useTaskOperations(projectId: string | null): {
  handleSubtaskCreate: (taskId: string) => void;
  handleSubtaskDelete: (taskId: string, subtaskId: string) => void;
  handleSubtaskUpdate: (taskId: string, subtaskId: string, updates: Partial<SubtaskProps>) => void;
  handleTaskCreate: (status: TaskStatus) => void;
  handleTaskDelete: (taskId: string) => void;
  handleTaskSelect: (task: TaskProps) => void;
  handleTaskUpdate: (taskId: string, updates: Partial<TaskProps>) => Promise<void>;
} {
  const queryClient = useQueryClient();
  const { createTask, deleteTask, tasks = [], updateTask } = useTasks(projectId);

  const { editingTaskId, selectedTaskId, setEditingTask, setSelectedTask } = useUIStore();

  const { createSubtask, deleteSubtask, updateSubtask } = useSubtasks(projectId, selectedTaskId);

  const { clearOptimisticUpdate, setOptimisticUpdate } = useLoadingStore();

  const handleTaskCreate = useCallback(
    (status: TaskStatus) => {
      // Don't allow task creation for unsaved (temp) projects
      if (!projectId || projectId.startsWith('temp-')) {
        logger.warn(
          { component: 'useTaskOperations', projectId },
          'Cannot create task: project not saved yet',
        );
        return;
      }

      const tempId = `temp-${Date.now()}`;

      // Only create optimistic UI state, no API call yet
      queryClient.setQueryData(['tasks', projectId], (oldTasks: TaskProps[] = []) => {
        const newTask: TaskProps = {
          id: tempId,
          projectId,
          title: '',
          status,
          priority: 'MEDIUM',
          description: undefined,
          prompt: undefined,
          subtasks: [],
          isEditing: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        return [...oldTasks, newTask];
      });
      setEditingTask(tempId);
    },
    [projectId, queryClient, setEditingTask],
  );

  const handleTaskUpdate = useCallback(
    async (taskId: string, updates: Partial<TaskProps>) => {
      logger.debug({ component: 'useTaskOperations' }, 'handleTaskUpdate called', {
        taskId,
        updates,
      });

      logger.debug({ component: 'useTaskOperations', taskId }, 'Starting handleTaskUpdate', {
        updates,
        updateType:
          'status' in updates
            ? 'status change'
            : 'title' in updates
              ? 'title change'
              : 'prompt' in updates
                ? 'prompt change'
                : 'subtasks' in updates
                  ? 'subtasks change'
                  : 'other',
      });

      const task = tasks.find(t => t.id === taskId);
      if (!task) {
        logger.warn({ component: 'useTaskOperations', taskId }, 'Task not found');
        return;
      }

      logger.debug({ component: 'useTaskOperations' }, 'Original task', {
        task,
      });

      const { isEditing, subtasks, ...apiUpdates } = updates;
      logger.debug({ component: 'useTaskOperations', taskId }, 'Extracted updates', {
        isEditing,
        subtasks,
        apiUpdates,
      });

      logger.debug({ component: 'useTaskOperations' }, 'API updates before processing', {
        apiUpdates,
      });

      // Update edit mode state immediately
      if ('isEditing' in updates) {
        logger.debug({ component: 'useTaskOperations', taskId }, 'Updating edit mode', {
          isEditing: updates.isEditing,
        });
        setEditingTask(updates.isEditing ? taskId : null);
      }

      if (task.id.startsWith('temp-') && !updates.isEditing) {
        logger.debug({ component: 'useTaskOperations', taskId }, 'Handling temporary task save');

        // Don't create tasks for unsaved (temp) projects
        if (task.projectId.startsWith('temp-')) {
          logger.warn(
            { component: 'useTaskOperations', taskId },
            'Cannot save task for unsaved project - save the project first',
          );
          return;
        }

        const title = apiUpdates.title || task.title;
        if (!title.trim()) {
          return;
        }

        const createTaskData: CreateTaskPayload = {
          projectId: task.projectId,
          title: title.trim(),
          status: task.status as TaskStatus,
          priority: task.priority as TaskPriority,
          description: apiUpdates.description || undefined,
          prompt: apiUpdates.prompt || undefined,
        };

        logger.debug({ component: 'useTaskOperations' }, 'Creating new task', {
          createTaskData,
        });

        // For temporary tasks, we don't need optimistic updates since the task already exists in UI.
        // Note: useTasks.createTask already has an onSuccess that replaces the temp task with
        // the real one in the cache — we only add onError here to clean up on failure.
        createTask.mutate(createTaskData, {
          onError: (): void => {
            // On error, remove the temporary task
            queryClient.setQueryData(['tasks', projectId], (oldTasks: TaskProps[] = []) => {
              return oldTasks.filter(t => t.id !== taskId);
            });
            setEditingTask(null);
          },
        });
        return;
      }

      if (Object.keys(apiUpdates).length > 0) {
        logger.debug({ component: 'useTaskOperations', taskId }, 'Processing API updates', {
          apiUpdates,
        });

        const typedUpdates: UpdateTaskPayload = {
          ...(apiUpdates.title !== undefined && { title: apiUpdates.title }),
          ...(apiUpdates.description !== undefined && {
            description: apiUpdates.description === null ? undefined : apiUpdates.description,
          }),
          ...(apiUpdates.status !== undefined && {
            status: apiUpdates.status as TaskStatus,
          }),
          ...(apiUpdates.priority !== undefined && {
            priority: apiUpdates.priority as TaskPriority,
          }),
          ...(apiUpdates.prompt !== undefined && {
            prompt: apiUpdates.prompt === null ? undefined : apiUpdates.prompt,
          }),
        };

        logger.debug({ component: 'useTaskOperations' }, 'Final typedUpdates for API', {
          typedUpdates,
        });

        if (
          !task.id.startsWith('temp-') &&
          typedUpdates.title !== undefined &&
          (!typedUpdates.title || typedUpdates.title.trim() === '')
        ) {
          logger.debug(
            { component: 'useTaskOperations', taskId },
            'Skipping update due to empty title',
          );
          clearOptimisticUpdate(`task-${taskId}`);
          return;
        }

        // Apply optimistic update immediately
        const updatedTask = { ...task, ...apiUpdates };
        logger.debug({ component: 'useTaskOperations' }, 'Applying optimistic update', {
          before: task,
          after: updatedTask,
          diff: Object.keys(apiUpdates),
        });

        queryClient.setQueryData(['tasks', projectId], (oldTasks: TaskProps[] = []) =>
          oldTasks.map(t => (t.id === taskId ? updatedTask : t)),
        );

        // Call the mutation
        logger.debug({ component: 'useTaskOperations' }, 'About to call mutation', {
          taskId: taskId,
          updates: typedUpdates,
        });

        if (!projectId) {
          logger.error(
            { component: 'useTaskOperations' },
            'Cannot update task: projectId is null',
            undefined,
            { taskId },
          );
          return;
        }

        try {
          updateTask.mutate(
            {
              projectId: projectId,
              taskId: taskId,
              updates: typedUpdates,
            },
            {
              onSuccess: (data): void => {
                logger.debug({ component: 'useTaskOperations' }, 'Task updated successfully', {
                  data,
                });
                // Invalidate both tasks and projects queries to update counts
                void queryClient.invalidateQueries({
                  queryKey: ['tasks', projectId],
                });
                void queryClient.invalidateQueries({ queryKey: ['projects'] });
                // Invalidate single-task query so PromptModal sees fresh prompt when re-opened
                void queryClient.invalidateQueries({
                  queryKey: ['task', projectId, taskId],
                });
              },
              onError: (error): void => {
                logger.error({ component: 'useTaskOperations' }, 'Task update failed', error);
                // On error, revert to the original task
                queryClient.setQueryData(['tasks', projectId], (oldTasks: TaskProps[] = []) =>
                  oldTasks.map(t => (t.id === taskId ? task : t)),
                );
              },
              onSettled: (): void => {
                logger.debug({ component: 'useTaskOperations' }, 'Mutation settled');
              },
            },
          );
          logger.debug({ component: 'useTaskOperations' }, 'Mutation call completed');
        } catch (error) {
          logger.error(
            { component: 'useTaskOperations' },
            'Error calling mutation',
            error as Error,
          );
        }
      } else {
        logger.debug({ component: 'useTaskOperations' }, 'No API updates to process');
      }

      // Handle subtask updates separately
      if (subtasks) {
        logger.debug({ component: 'useTaskOperations' }, 'Handling subtask updates', {
          subtasks: subtasks.map(s => ({
            id: s.id,
            isEditing: s.isEditing,
            title: s.title,
          })),
        });

        // Create a stable reference to the updated task
        const updatedTask = {
          ...task,
          subtasks: subtasks.map(s => {
            const updatedSubtask = {
              ...s,
              // Preserve isEditing exactly as it is
              isEditing: 'isEditing' in s ? s.isEditing : false,
            };
            logger.debug(
              { component: 'useTaskOperations', taskId, subtaskId: s.id },
              'Updating subtask in cache',
              {
                id: s.id,
                isEditingBefore: s.isEditing,
                isEditingAfter: updatedSubtask.isEditing,
              },
            );
            return updatedSubtask;
          }),
        };

        logger.debug({ component: 'useTaskOperations' }, 'Updated task with subtasks', {
          taskId: updatedTask.id,
          subtasks: updatedTask.subtasks.map(s => ({
            id: s.id,
            isEditing: s.isEditing,
            title: s.title,
          })),
        });

        // Update ONLY the tasks list cache
        queryClient.setQueryData(['tasks', projectId], (oldTasks: TaskProps[] = []) => {
          logger.debug(
            { component: 'useTaskOperations', taskId },
            'Updating [tasks] cache for subtask edit',
          );
          return oldTasks.map(t => (t.id === taskId ? updatedTask : t));
        });
      }
    },
    [tasks, createTask, updateTask, setEditingTask, clearOptimisticUpdate, projectId, queryClient],
  );

  const handleTaskDelete = useCallback(
    (taskId: string) => {
      // For temporary tasks, just remove them from the cache without API calls
      if (taskId.startsWith('temp-')) {
        logger.debug({ component: 'useTaskOperations' }, 'Deleting temporary task locally', {
          taskId,
        });

        // Update tasks cache to remove the temp task
        queryClient.setQueryData<TaskProps[]>(['tasks', projectId], (oldTasks = []) =>
          oldTasks.filter(task => task.id !== taskId),
        );

        // Clear selection if this was the selected task
        if (selectedTaskId === taskId) {
          setSelectedTask(null);
        }

        // Clear editing state if needed
        if (editingTaskId === taskId) {
          setEditingTask(null);
        }

        return;
      }

      // For regular tasks, proceed with API deletion
      if (!projectId) {
        logger.error(
          { component: 'useTaskOperations' },
          'Cannot delete task: projectId is null',
          undefined,
          { taskId },
        );
        return;
      }

      setOptimisticUpdate(`task-delete-${taskId}`, true);

      deleteTask.mutate(
        { projectId: projectId, taskId },
        {
          onSuccess: (): void => {
            if (selectedTaskId === taskId) {
              setSelectedTask(null);
            }
            setEditingTask(null);
            clearOptimisticUpdate(`task-delete-${taskId}`);
            // Invalidate both tasks and projects queries to update counts
            void queryClient.invalidateQueries({
              queryKey: ['tasks', projectId],
            });
            void queryClient.invalidateQueries({ queryKey: ['projects'] });
          },
          onError: (): void => {
            clearOptimisticUpdate(`task-delete-${taskId}`);
          },
        },
      );
    },
    [
      deleteTask,
      selectedTaskId,
      editingTaskId,
      projectId,
      queryClient,
      setSelectedTask,
      setEditingTask,
      setOptimisticUpdate,
      clearOptimisticUpdate,
    ],
  );

  const handleTaskSelect = useCallback(
    (task: TaskProps) => {
      setSelectedTask(task.id);
    },
    [setSelectedTask],
  );

  const handleSubtaskCreate = useCallback(
    (taskId: string) => {
      logger.debug({ component: 'useTaskOperations', taskId }, 'handleSubtaskCreate called');

      // Get the task from the tasks cache to ensure it exists
      const tasksCache = queryClient.getQueryData<TaskProps[]>(['tasks', projectId]);
      if (!tasksCache) {
        logger.error({ component: 'useTaskOperations', taskId }, 'Tasks cache not found');
        return;
      }
      const task = tasksCache.find(t => t.id === taskId);
      if (!task) {
        logger.error({ component: 'useTaskOperations', taskId }, 'Task not found in cache');
        return;
      }

      // Create a temporary ID
      const tempId = `temp-${Date.now()}`;
      const now = new Date();

      // Create the *data* for the new subtask (without isEditing logic)
      const newSubtaskData: Subtask = {
        id: tempId,
        taskId,
        title: '',
        description: '',
        completed: false,
        createdAt: now.toISOString(),
        updatedAt: now.toISOString(),
      };

      // Add isEditing: false to satisfy TaskWithUI type for caching
      const newSubtaskForCache: SubtaskProps = {
        ...newSubtaskData,
        isEditing: false,
      };

      logger.debug(
        { component: 'useTaskOperations' },
        'Prepared temporary subtask data for cache',
        { newSubtaskForCache },
      );

      // Create updated task with new subtask *data*
      const updatedTask: TaskProps = {
        ...task,
        // Add the new subtask data (with isEditing: false) to the existing subtasks
        subtasks: [...(task.subtasks || []), newSubtaskForCache],
      };

      // Update the tasks list cache optimistically with the new subtask data
      queryClient.setQueryData<TaskProps[]>(['tasks', projectId], (oldTasks = []) => {
        logger.debug({ component: 'useTaskOperations' }, 'Updating tasks cache for subtask create');
        return oldTasks.map(t => (t.id === taskId ? updatedTask : t));
      });

      // NOTE: We don't call createSubtask.mutate here.
      // The actual API call happens when the user saves the title in handleSubtaskUpdate.
      logger.debug(
        { component: 'useTaskOperations' },
        'Optimistically added temp subtask to cache. API call deferred until save',
      );

      // Optional: We could still update ['subtasks', taskId] cache if needed elsewhere,
      // but let's keep it simple and rely on the main ['tasks'] update for now.
      // queryClient.setQueryData(['subtasks', taskId], updatedTask.subtasks);
    },
    [projectId, queryClient],
  );

  const handleSubtaskUpdate = useCallback(
    // Update type to match SubtaskList: only data fields
    (
      taskId: string,
      subtaskId: string,
      updates: Partial<Omit<Subtask, 'id' | 'taskId' | 'createdAt' | 'updatedAt'>>,
    ) => {
      logger.debug({ component: 'useTaskOperations' }, 'Starting handleSubtaskUpdate (data only)', {
        taskId,
        subtaskId,
        updates,
        updateKeys: Object.keys(updates),
      });

      // Get current state from cache
      const tasksCache = queryClient.getQueryData<TaskProps[]>(['tasks', projectId]);
      const currentTask = tasksCache?.find(t => t.id === taskId);
      // Get subtasks directly from the task object found in the tasks cache
      const currentSubtasks = currentTask?.subtasks;

      if (!currentTask) {
        logger.error(
          { component: 'useTaskOperations' },
          'Task not found in tasks cache',
          undefined,
          { taskId },
        );
        return;
      }
      // Check if subtasks array exists on the task
      if (!currentSubtasks) {
        logger.error(
          { component: 'useTaskOperations' },
          'Subtasks array not found on task object in cache',
          undefined,
          { taskId },
        );
        return;
      }

      // Find the target subtask within the task's subtasks array
      const subtask = currentSubtasks.find(s => s.id === subtaskId);
      if (!subtask) {
        logger.warn({ component: 'useTaskOperations' }, 'Subtask not found', {
          subtaskId,
        });
        return;
      }

      logger.debug({ component: 'useTaskOperations' }, 'Current state', {
        task: currentTask.id,
        subtask: subtask.id,
        isEditing: subtask.isEditing,
      });

      // Create updated subtasks array by applying *only data* updates
      const updatedSubtasks = currentSubtasks.map(s => {
        if (s.id === subtaskId) {
          // Apply the received data updates
          return { ...s, ...updates };
        }
        return s; // Other subtasks remain unchanged
      });

      // Create updated task
      const updatedTask = {
        ...currentTask,
        subtasks: updatedSubtasks,
      };

      logger.debug({ component: 'useTaskOperations' }, 'Prepared updates', {
        taskId,
        subtaskId,
        updatedSubtasks: updatedSubtasks.map(s => ({
          id: s.id,
          title: s.title,
          completed: s.completed,
        })),
      });

      // --- Simplified Cache Update ---
      // We will ONLY update the main tasks list cache, as this is the primary source for the UI.
      // Updates to ['task', taskId] and ['subtasks', taskId] seemed to cause flickering.

      // Update ONLY the tasks list cache
      queryClient.setQueryData(['tasks', projectId], (oldTasks: TaskProps[] = []) => {
        logger.debug({ component: 'useTaskOperations' }, 'Updating tasks cache for subtask edit');
        return oldTasks.map(t => (t.id === taskId ? updatedTask : t));
      });

      // Remove separate updates:
      // queryClient.setQueryData(['task', taskId], updatedTask);
      // queryClient.setQueryData(['subtasks', taskId], updatedSubtasks);

      // --- End Simplified Cache Update ---

      // If this is a temporary subtask being saved (identified by having a title update)
      if (subtaskId.startsWith('temp-') && updates.title !== undefined) {
        logger.debug(
          { component: 'useTaskOperations' },
          'Saving temporary subtask via handleSubtaskUpdate',
          { subtaskId },
        );
        const now = new Date();
        // Prepare the data for the API call
        const newSubtaskData: CreateSubtaskPayload = {
          taskId,
          // Use the provided title, ensure it's trimmed, default if somehow empty
          title: updates.title?.trim() || 'Untitled',
          // Apply other potential updates or use defaults
          completed: updates.completed ?? false,
          description: updates.description ?? undefined, // API expects undefined or string
          // createdAt/updatedAt are set by the server
        };

        if (!projectId) {
          logger.error(
            { component: 'useTaskOperations' },
            'Cannot create subtask: projectId is null',
            undefined,
            { taskId, subtaskId },
          );
          return;
        }

        createSubtask.mutate(
          {
            projectId: projectId,
            taskId,
            subtask: {
              title: newSubtaskData.title,
              completed: newSubtaskData.completed,
              description: newSubtaskData.description,
              createdAt: now.toISOString(),
              updatedAt: now.toISOString(),
            },
          },
          {
            onSuccess: (createdSubtask): void => {
              logger.debug(
                { component: 'useTaskOperations' },
                'Temp subtask created successfully',
                {
                  tempSubtaskId: subtaskId,
                  createdSubtaskId: createdSubtask.id,
                },
              );
              // Replace the temp subtask with the real one (without isEditing)
              const finalSubtasks = updatedSubtasks.map(
                s => (s.id === subtaskId ? createdSubtask : s), // Use the created subtask directly
              );

              // Update ONLY the tasks list cache with the final state
              const finalTask = { ...updatedTask, subtasks: finalSubtasks };
              queryClient.setQueryData(['tasks', projectId], (oldTasks: TaskProps[] = []) => {
                logger.debug(
                  { component: 'useTaskOperations' },
                  'Updating tasks cache after temp subtask save',
                );
                return oldTasks.map(t => (t.id === taskId ? finalTask : t));
              });
              // Remove separate updates:
              // queryClient.setQueryData(['task', taskId], finalTask);
              // queryClient.setQueryData(['subtasks', taskId], finalSubtasks);
            },
          },
        );
        return; // Important: Return after handling temp subtask save
      }

      // For real subtasks, if we have API updates, send them
      if (!subtaskId.startsWith('temp-')) {
        // Prepare updates for the API, excluding fields not allowed by the API type
        // (Note: 'updates' already has the correct type from the function signature)
        const apiUpdates = { ...updates };

        // Remove description if it's explicitly null (API might expect undefined or omission)
        if (apiUpdates.description === null) {
          delete apiUpdates.description;
        }

        if (Object.keys(apiUpdates).length > 0) {
          if (!projectId) {
            logger.error(
              { component: 'useTaskOperations' },
              'Cannot update subtask: projectId is null',
              undefined,
              { taskId, subtaskId },
            );
            return;
          }

          logger.debug({ component: 'useTaskOperations' }, 'Calling updateSubtask mutation', {
            subtaskId,
            apiUpdates,
          });
          updateSubtask.mutate({
            projectId: projectId,
            taskId,
            subtaskId,
            updates: apiUpdates, // Pass the prepared updates
          });
        } else {
          logger.debug(
            { component: 'useTaskOperations' },
            'No data changes detected, mutation skipped',
            { subtaskId },
          );
        }
      }
    },
    [projectId, queryClient, createSubtask, updateSubtask], // Dependencies remain the same
  );

  const handleSubtaskDelete = useCallback(
    (taskId: string, subtaskId: string) => {
      const task = tasks.find(t => t.id === taskId);
      if (!task) {
        return;
      }

      // If it's a temporary subtask, just remove it from UI
      if (subtaskId.startsWith('temp-')) {
        const updatedSubtasks = task.subtasks.filter(s => s.id !== subtaskId) as SubtaskProps[]; // Cast result
        // .map(s => ({ ...s, isEditing: false })) as SubtaskWithUI[]; // Remove isEditing manipulation
        void handleTaskUpdate(taskId, { subtasks: updatedSubtasks });
        return;
      }

      // For real subtasks, delete from API and update UI immediately
      if (!projectId) {
        logger.error(
          { component: 'useTaskOperations' },
          'Cannot delete subtask: projectId is null',
          undefined,
          { taskId, subtaskId },
        );
        return;
      }

      const updatedSubtasks = task.subtasks.filter(s => s.id !== subtaskId) as SubtaskProps[]; // Cast result
      // .map(s => ({ ...s, isEditing: false })) as SubtaskWithUI[]; // Remove isEditing manipulation
      void handleTaskUpdate(taskId, { subtasks: updatedSubtasks });

      deleteSubtask.mutate(
        { projectId: projectId, taskId, subtaskId },
        {
          onError: (): void => {
            // If API call fails, revert the UI change
            // const revertedSubtasks = task.subtasks
            //   .map(s => ({ ...s, isEditing: false })) as SubtaskWithUI[]; // Remove isEditing manipulation
            const revertedSubtasks = task.subtasks as SubtaskProps[]; // Cast result
            void handleTaskUpdate(taskId, { subtasks: revertedSubtasks });
          },
        },
      );
    },
    [tasks, deleteSubtask, handleTaskUpdate, projectId],
  );

  return {
    handleTaskCreate,
    handleTaskUpdate,
    handleTaskDelete,
    handleTaskSelect,
    handleSubtaskCreate,
    handleSubtaskUpdate,
    handleSubtaskDelete,
  };
}
